import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseRepository } from '../Common/Data/base.repository';
import { TopLevelCompanyData } from './actionView.repository';

@Injectable({ providedIn: 'root' })
export class LitigationReferralRepository extends BaseRepository {
    constructor(httpClient: HttpClient) {
        super(httpClient);
    }

    private _baseUrl: string = '/api/LitigationReferral';

    getCompanyLitigationReferrals(companyId: number): Observable<Core.LitigationReferralListModel[]> {
        return this.httpGet(`/api/company/${companyId}/LitigationReferrals`);
    }

    getSiteLitigationReferrals(siteId: number): Observable<Core.LitigationReferralListModel[]> {
        return this.httpGet(`/api/site/${siteId}/LitigationReferrals`);
    }

    getShowCreateReferralButton(siteId: number): Observable<number> {
        return this.httpGet(`/api/site/${siteId}/showCreateReferralButton`);
    }

    createLitigationReferralBySite(siteId: number): Observable<Core.LitigationReferralCreateResult> {
        return this.httpPost(`${this._baseUrl}/create/${siteId}`, {});
    }

    createLitigationReferralByTaskIds(taskIds: number[]): Observable<Core.LitigationReferralCreateResult> {
        return this.httpPost(`${this._baseUrl}/create/tasks`, taskIds);
    }

    bulkValidate(taskIds: number[]): Observable<Core.BulkOperationResult[]> {
        return this.httpPost(`${this._baseUrl}/bulkvalidate`, taskIds);
    }

    bulkTransferToRLS(taskIds: number[]): Observable<Core.BulkOperationResult[]> {
        return this.httpPost(`${this._baseUrl}/bulktransfer`, taskIds);
    }

    bulkCompleteDraft(taskIds: number[]): Observable<Core.BulkOperationResult[]> {
        return this.httpPost(`${this._baseUrl}/bulkcompletedraft`, taskIds);
    }

    getLitigationReferral(referralId: number): Observable<Core.LitigationReferralModel> {
        return this.httpGet(`${this._baseUrl}/${referralId}`);
    }

    getLitigationReferralByTaskId(taskId: number): Observable<Core.LitigationReferralModel> {
        return this.httpGet(`${this._baseUrl}/task/${taskId}`);
    }

    getLitigationReferralLiveData(referralId: number): Observable<Core.LitigationReferralModel> {
        return this.httpGet(`${this._baseUrl}/${referralId}/live`);
    }

    getLitigationReferralOriginalData(referralId: number): Observable<Core.LitigationReferralModel> {
        return this.httpGet(`${this._baseUrl}/${referralId}/original`);
    }

    getLitigationReferralSiteClasses(): Observable<Core.LitigationReferralSiteClassesModel> {
        return this.httpGet(`${this._baseUrl}/siteClasses`);
    }

    getCoCounselFirms(includeCoCounselFirmIds: number[] = []): Observable<Core.CoCounselFirmModel[]> {
        let params = new HttpParams();

        includeCoCounselFirmIds.filter(Boolean).forEach((id) => {
            params = params.append('includeCoCounselFirmIds', id.toString());
        });

        return this.httpGet(`${this._baseUrl}/coCounselFirms`, { params });
    }

    updateLitigationReferral(referralModel: Core.LitigationReferralModel): Observable<void> {
        return this.httpPut(`${this._baseUrl}/${referralModel.litigationReferralId}`, referralModel);
    }

    deleteLitigationReferral(referralId: number): Observable<void> {
        return this.httpDelete(`${this._baseUrl}/${referralId}`);
    }

    getLitigationReferralEligibleAppeals(referralId: number): Observable<Core.LitigationReferralEligibleAppealModel[]> {
        return this.httpGet(`${this._baseUrl}/${referralId}/appeals/eligible`);
    }

    addAppealsToLitigationReferral(referralId: number, appealIds: number[]): Observable<void> {
        return this.httpPost(`${this._baseUrl}/${referralId}/appeals/add`, appealIds);
    }

    removeAppealsFromLitigationReferral(referralId: number, appealIds: number[]): Observable<void> {
        return this.httpPost(`${this._baseUrl}/${referralId}/appeals/remove`, appealIds);
    }

    syncAllAppeals(referralId: number): Observable<void> {
        return this.httpPost(`${this._baseUrl}/${referralId}/appeals/sync`, {});
    }

    setPrimaryAppeal(referralId: number, appealId: number): Observable<void> {
        return this.httpPost(`${this._baseUrl}/${referralId}/appeal/${appealId}/setPrimary`, {});
    }

    getReferralDataForEngagement(engagementNumberAH: string): Observable<Core.LitigationReferralEngagementDataModel> {
        return this.httpGet(`${this._baseUrl}/engagement?engagementNumberAH=${engagementNumberAH}`);
    }

    areLitigationReferralsAllowedForCompany(tlCompanyId: number): Observable<boolean> {
        return this.httpGet(`${this._baseUrl}/company/${tlCompanyId}/isAllowed`);
    }

    areLitigationReferralsAllowedForAppeal(appealId: number): Observable<boolean> {
        return this.httpGet(`${this._baseUrl}/appeal/${appealId}/isAllowed`);
    }

    getAppealLitigationReferralStatus(appealId: number): Observable<Core.AppealLitigationReferralStatusModel> {
        return this.httpGet(`${this._baseUrl}/appeal/${appealId}/status`);
    }

    areAnyLitigationReferralsPendingForCompany(tlCompanyId: number): Observable<boolean> {
        return this.httpGet(`${this._baseUrl}/company/${tlCompanyId}/pending`);
    }

    getLitigationReferralWorkFlowStatus(
        litigationReferralId: number,
    ): Observable<Core.LitigationReferralWorkflowStatusModel> {
        return this.httpGet(`${this._baseUrl}/${litigationReferralId}/workflowstatus`);
    }

    completeLitigationReferralTask(
        litigationReferralId: number,
        litigationReferralTaskUpdateModel: Core.LitigationReferralTaskUpdateModel,
    ): Observable<Core.LitigationReferralTaskUpdateResultModel> {
        return this.httpPost(
            `${this._baseUrl}/${litigationReferralId}/task/complete`,
            litigationReferralTaskUpdateModel,
        );
    }

    skipLitigationReferralTask(
        litigationReferralId: number,
        litigationReferralTaskUpdateModel: Core.LitigationReferralTaskUpdateModel,
    ): Observable<Core.LitigationReferralTaskUpdateResultModel> {
        return this.httpPost(`${this._baseUrl}/${litigationReferralId}/task/skip`, litigationReferralTaskUpdateModel);
    }

    rescindLitigationReferralTask(
        litigationReferralId: number,
        litigationReferralTaskUpdateModel: Core.LitigationReferralTaskUpdateModel,
    ): Observable<Core.LitigationReferralTaskUpdateResultModel> {
        return this.httpPost(
            `${this._baseUrl}/${litigationReferralId}/task/rescind`,
            litigationReferralTaskUpdateModel,
        );
    }

    reassignLitigationReferralTask(
        litigationReferralId: number,
        litigationReferralTaskUpdateModel: Core.LitigationReferralTaskUpdateModel,
    ): Observable<Core.LitigationReferralTaskUpdateResultModel> {
        return this.httpPost(
            `${this._baseUrl}/${litigationReferralId}/task/reassign`,
            litigationReferralTaskUpdateModel,
        );
    }

    insertAddToLitigationReferralToTaskSeries(appealId: number): Observable<boolean> {
        return this.httpGet(`${this._baseUrl}/appeal/${appealId}/task/insert`);
    }

    removeAddFromLitigationReferralFromTaskSeries(appealId: number): Observable<boolean> {
        return this.httpGet(`${this._baseUrl}/appeal/${appealId}/task/remove`);
    }

    getTopLevelCompanyIdsByAssessmentTaskIds(taskIds: number[]): Observable<TopLevelCompanyData[]> {
        return this.httpPost(`${this._baseUrl}/TopLevelCompanyIdsByAssessmentTaskIds`, taskIds);
    }
}
